import { z } from "zod";

/**
 * SPEC-67 §3.4 step 2 (r1 BUG #7) — the refresh-token payload parsed
 * EXPLICITLY before any field access.
 *
 * `verifyToken` returns `payload as unknown as Session` with no narrowing, so
 * `did`/`jti` are typed `string | undefined` at the handler; a careless
 * `payload.did.toUpperCase()` would compile and throw at runtime. Parsing with
 * this schema converts the contract into data: a parse failure → generic 401.
 *
 * `did: ""` and an ABSENT `did` are deliberately different outcomes (§3.10):
 *   - malformed (empty string, non-uuid) → parse failure → 401 unauthorized;
 *   - absent-but-well-formed (pre-SPEC-67 token) → parses fine (`did` is
 *     optional) → the ROUTE then answers `device_binding_required`.
 */
export const RefreshPayload = z.object({
  userId: z.string().uuid(),
  rv: z.number().int().nonnegative(),
  type: z.literal("refresh"),
  did: z.string().uuid().optional(), // absent on pre-SPEC-67 tokens
  jti: z.string().min(1).optional(),
  /**
   * SPEC-68 §3.5 — session class. `true` = full chain (7 d), `false` = fallback
   * chain (12 h). ABSENT is meaningful: the token predates SPEC-68, so the
   * route resolves it via `pkGrandfathered(iat)` — a TIME-BOUNDED grandfather,
   * never a blanket `?? true` (r2 BUG #1). See §3.5's three-row table.
   */
  pk: z.boolean().optional(),
});

export type RefreshPayload = z.infer<typeof RefreshPayload>;
